import { Button, Tag } from 'antd';
import { Plug, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import type { AdapterCatalogueState } from '../hooks/use-adapter-catalogue';
import { installIntegrationPath } from '../integrations.paths';
import { AdapterLogo } from './AdapterLogo';

const CAPABILITY_LABELS = { webhook: 'Webhooks', polling: 'Polling', deviceListing: 'Device list', geofencePush: 'Geofence push' } as const;

/** The GPS vendors this release ships. Installing one opens its form, rendered from the adapter's config fields. */
export function AdapterCatalogue({ catalogue }: { catalogue: AdapterCatalogueState }) {
  if (catalogue.status === 'loading') return <CatalogueSkeleton />;
  if (catalogue.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the catalogue" description="Something went wrong loading the GPS adapters. Try reloading the page." />
    );
  }
  if (catalogue.adapters.length === 0) {
    return (
      <EmptyState
        icon={Plug}
        title="No GPS adapters in this release"
        description="A vendor's adapter ships with a release and appears here to install with your company's credentials."
      />
    );
  }

  return (
    <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {catalogue.adapters.map((adapter) => (
        <li key={adapter.key} className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-5">
          <div className="flex items-center gap-3">
            <AdapterLogo logoSvg={adapter.logoSvg} name={adapter.displayName} />
            <h3 className="text-base font-semibold text-fg">{adapter.displayName}</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {adapter.capabilities.map((capability) => (
              <Tag key={capability}>{CAPABILITY_LABELS[capability]}</Tag>
            ))}
          </div>
          <Link to={installIntegrationPath(adapter.key)} className="mt-auto">
            <Button type="primary">Install</Button>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function CatalogueSkeleton() {
  return (
    <SkeletonRegion label="Loading the GPS catalogue" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {[0, 1, 2].map((card) => (
        <div key={card} className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-5">
          <div className="flex items-center gap-3">
            <Skeleton className="size-10" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </SkeletonRegion>
  );
}
